import { Container, Sprite } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { type ReelAnticipationFrame } from "@slot/mechanics-reels";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { CaptainView } from "../views/character/CaptainView";
import { SYMBOL_CATALOG } from "../assets/symbols/symbolCatalog";
import { SYMBOL_SPINE_CATALOG } from "../assets/symbols/symbolSpineCatalog";
import { RuntimeSymbolCatalog } from "../assets/symbols/RuntimeSymbolCatalog";
import { WinMeterView } from "../views/win-meter/WinMeterView";
import { AnticipationFrameView } from "../presentation/AnticipationFrameView";
import { AnticipationBackground } from "../presentation/AnticipationBackground";
import { createWinAmountLabelFactory } from "../composition/createWinAmountLabel";
import { createRemovalEffectSpawner } from "../composition/createRemovalEffectSpawner";
import { GameBackground } from "./GameBackground";
import { GridContainer } from "./GridContainer";
import { CannonView } from "../features/cannon/CannonView";
import { CannonBulletView } from "../features/cannon/CannonBulletView";
import { ChanceMarkersView } from "../features/chance/ChanceMarkersView";
import { CHANCE_TUNING } from "../features/chance/chanceTuning";
import { SCATTER_LIGHT_TUNING } from "../tuning/scatterLightTuning";
import { SpriteText, SpriteTextFitMode, fitWithin } from "@slot/presentation";
import type { ContentAreaStore, FitRule, Log, PresentationTaskRunner, PresentationTicker, SpineDriver, SymbolRemovalBurstSpawner, SymbolSpineSource, ViewportStore, WinAmountLabelFactory } from "@slot/presentation";
import { LocalizedText } from "@slot/game-shell";
import type { FeatureStateStore, GameLogoSpec, LocalizationService } from "@slot/game-shell";

/**
 * The composition root: 1015.9×1004 authored. Desktop and portrait fit that box into the viewport
 * under the authored limits and lift it by the shape's offset (the bar overlays the composition's
 * bottom margin). Mobile landscape fits the VISIBLE art into the content area between the bars: the
 * frame with its top ornament (±640 wide, −545 up) down to the cannon's base (656 below the origin),
 * measured on the rendered scene — 1280×1205, its centre 57.5 below the root's origin.
 */
export const COMPOSITION_ROOT = {
  width: 1015.916,
  height: 1004.022,
  fit: {
    box: {
      desktop: { width: 1015.916, height: 1004.022 },
      mobile: { width: 1015.916, height: 1004.022 },
      mobile_landscape: { width: 1280, height: 1205, centerY: 57.5 },
    },
    limits: { desktop: { maxWidth: 1400 }, mobile: { maxWidth: 1100 }, mobile_landscape: {} },
    offsetY: { desktop: -80, mobile: -200, mobile_landscape: 0 },
  },
} as const satisfies { width: number; height: number; fit: FitRule };
/**
 * `BackdropLayer/BrandedIconLayer` from the prefab dump: the logo's box and sprite, its spot per shape; on desktop
 * the authored spot pokes outside the composition box and is nudged back in on X.
 */
export const GAME_LOGO: GameLogoSpec = {
  box: { width: 363.1, height: 254.4 },
  sprite: { width: 363.4, height: 248 },
  placement: {
    desktop: { x: -763.503, y: -453.571, keepInsideX: true },
    mobile: { x: -326.198, y: -638.563 },
    mobile_landscape: { x: -777.107, y: -361.609, scale: 0.7 },
  },
};
/** `HudLayer/WinMeterView` (y −520.73 up) with its `root` (−33.57) and `SpriteText` (−2.41) offsets. */
const WIN_METER = { y: -420, textY: 33.567 + 2.41, boxWidth: 355, boxHeight: 100 } as const;
/**
 * The tip line while the plank has no total: one line of gold on the plank's lower board (`dropY` below the total's
 * line), shrunk to fit its box.
 */
const WIN_METER_TIP = { fontSize: 34, boxWidth: 760, boxHeight: 60, dropY: 25 } as const;

/**
 * The `GameScene` display tree: background layer, the composition root (grid container, HUD, FX
 * overlays), and the per-round factories the composition root hands to the runtime. Mirrors the
 * the authored scene node for node; every geometry constant is sourced from the prefab dump.
 */
export class GameScene extends Container {
  public readonly background: GameBackground;
  public readonly compositionRoot: Container;
  public readonly gridContainer: GridContainer;
  /** The chance detonation's target markers, under the symbols (`GridContainer.cellUnderlay`). */
  public readonly chanceMarkers: ChanceMarkersView;
  public readonly hudLayer: Container;
  public readonly fxOverlays: Container;
  public readonly winMeterView: WinMeterView;
  public readonly cannonView: CannonView;
  /** Builds one cannon round (the `cannonProjectile` prefab); null when its art is missing. */
  public readonly createCannonBullet: (() => CannonBulletView) | null;
  /** The light at the cannon's mouth on a scatter drop (`ScatterDropLight`), between the grid and the cannon; null without its art. */
  public readonly scatterLight: Sprite | null;
  public readonly symbolCatalog: RuntimeSymbolCatalog;
  public readonly symbolSpineSources: ReadonlyMap<string, SymbolSpineSource>;
  public readonly anticipationBackground: AnticipationBackground;
  public readonly createWinAmountLabel: WinAmountLabelFactory | null;
  public readonly createAnticipationFrame: (() => ReelAnticipationFrame) | null;
  public readonly removalEffectSpawner: SymbolRemovalBurstSpawner | null;
  /** The pirate captain behind the grid: empty until his art is in (`createScene` loads it). */
  public readonly captainView: CaptainView;
  private readonly disposers: Array<() => void> = [];

  public constructor(input: {
    assets: GameVisualAssets;
    chromeTaskRunner: PresentationTaskRunner;
    featureStateStore: FeatureStateStore;
    viewport: ViewportStore;
    contentArea: ContentAreaStore;
    presentationTicker: PresentationTicker;
    spineDriver: SpineDriver;
    /** The renderer's device pixels per CSS pixel: the art drawn into textures of its own is drawn at its size on the screen. */
    resolution: number;
    localization: LocalizationService;
    log: Log;
  }) {
    super({ label: "GameScene" });
    const { assets, log } = input;
    const chromeRunner: PresentationTaskRunner = input.chromeTaskRunner;

    // ── Background ────────────────────────────────────────────────────────────────────────────
    this.background = new GameBackground({ assets, spineDriver: input.spineDriver, log });
    this.background.bind(input.viewport);
    this.addChild(this.background);
    this.anticipationBackground = new AnticipationBackground({
      overlayTargets: [this.background.overlayTarget],
      chromeRunner,
    });

    // ── Composition root: fitted into the content area per shape ─────────────────────────────
    this.compositionRoot = new Container({ label: "GameCompositionRoot" });
    this.addChild(this.compositionRoot);

    // ── The pirate captain behind the grid, where the reference scene kept its inactive bear ──
    this.captainView = new CaptainView({ viewport: input.viewport, runner: chromeRunner, log });
    this.compositionRoot.addChild(this.captainView);

    this.disposers.push(
      input.contentArea.subscribe((area) => {
        const { shape, scale: cssPerUnit } = input.viewport.getSnapshot();
        const { scale, x, y } = fitWithin(COMPOSITION_ROOT.fit, shape, area);
        this.compositionRoot.scale.set(scale, scale);
        this.compositionRoot.position.set(x, y);
        this.captainView.setPixelsPerUnit(scale * cssPerUnit * input.resolution);
      }),
    );

    // ── Mechanic layer: the GridContainer prefab ─────────────────────────────────────────────
    const mechanicLayer = new Container({ label: "MechanicLayer" });
    this.gridContainer = new GridContainer({ baseFrame: assets.textures.baseGridFrame, featureFrame: assets.textures.featureGridFrame });
    this.gridContainer.bindFeatureState(input.featureStateStore);
    mechanicLayer.addChild(this.gridContainer);
    this.compositionRoot.addChild(mechanicLayer);
    this.chanceMarkers = new ChanceMarkersView(CHANCE_TUNING.marker);
    this.gridContainer.cellUnderlay.addChild(this.chanceMarkers);

    // FX overlays (win-amount labels) above the mechanic layer, in composition-root space.
    this.fxOverlays = new Container({ label: "FxOverlays" });
    this.compositionRoot.addChild(this.fxOverlays);

    // ── HUD layer: the win meter, and its tip line while it has no total ──────────────────────
    this.hudLayer = new Container({ label: "HudLayer" });
    this.compositionRoot.addChild(this.hudLayer);
    const goldenFont = assets.fonts.goldenFont;
    const winMeterText = goldenFont
      ? new SpriteText({ font: goldenFont, maxCharacters: 24, glyphScale: 2, fitMode: SpriteTextFitMode.Both, boxWidth: WIN_METER.boxWidth, boxHeight: WIN_METER.boxHeight, label: "SpriteText" })
      : null;
    // Pixi text, not the sprite font: the tips are sentences in every locale, and the golden font has capitals only.
    const winMeterTip = new LocalizedText({
      localization: input.localization,
      style: {
        fontFamily: "Roboto, system-ui, sans-serif",
        fontWeight: "700",
        fontSize: WIN_METER_TIP.fontSize,
        fill: 0xffe08a,
        stroke: { color: 0x3b1d06, width: 5 },
        dropShadow: { color: 0x000000, alpha: 0.6, blur: 4, distance: 2, angle: Math.PI / 2 },
        align: "center",
      },
      label: "WinMeterTip",
      fitBox: { width: WIN_METER_TIP.boxWidth, height: WIN_METER_TIP.boxHeight },
    });
    this.winMeterView = new WinMeterView({ spriteText: winMeterText, tip: winMeterTip, chromeRunner });
    this.winMeterView.position.set(0, WIN_METER.y);
    if (winMeterText) {
      winMeterText.position.set(0, WIN_METER.textY);
      this.winMeterView.addChild(winMeterText);
    }
    this.hudLayer.addChild(this.winMeterView);
    // The tip rides the grid (its overlay), so it bounces with the plank when a scatter or a barrel lands.
    winMeterTip.position.copyFrom(this.gridContainer.gridOverlay.toLocal({ x: 0, y: WIN_METER.y + WIN_METER.textY + WIN_METER_TIP.dropY }, this.hudLayer));
    this.gridContainer.gridOverlay.addChild(winMeterTip);

    // Cannon (HudLayer/CannonPanel): body + top Spines, rounds from the symbols atlas + goldenNumbers1.
    this.cannonView = new CannonView({
      skeletonData: assets.skeletons.get("animations/spine/cannon_bottom.skeleton"),
      topSkeletonData: assets.skeletons.get("animations/spine/cannon_top.skeleton"),
      spineDriver: input.spineDriver,
    });
    this.hudLayer.addChild(this.cannonView);
    const roundTexture = assets.symbols.textures["cannon_round"] ?? null;
    const numbersFont = assets.fonts.goldenNumbers1;
    this.createCannonBullet = roundTexture && numbersFont ? () => new CannonBulletView({ plateTexture: roundTexture, font: numbersFont }) : null;
    if (!this.createCannonBullet) log("[GameScene] cannon round art/font missing — the queue will not show");

    // The light at the cannon's mouth (`ScatterDropLight` flares it on a scatter drop), between the grid and the cannon
    // (user, 2026-10-15): right above the mechanic layer, so it lights the symbols up while the win labels and the HUD
    // (the cannon) draw over it; hidden until a scatter lands.
    const screenLight = assets.textures.screenLight;
    this.scatterLight = screenLight ? new Sprite({ texture: screenLight, label: "ScatterLight" }) : null;
    if (this.scatterLight) {
      this.scatterLight.anchor.set(0.5);
      this.scatterLight.blendMode = SCATTER_LIGHT_TUNING.blendMode;
      this.scatterLight.position.set(SCATTER_LIGHT_TUNING.x, SCATTER_LIGHT_TUNING.y);
      this.scatterLight.scale.set(SCATTER_LIGHT_TUNING.scale);
      this.scatterLight.alpha = 0;
      this.scatterLight.visible = false;
      this.compositionRoot.addChildAt(this.scatterLight, this.compositionRoot.getChildIndex(mechanicLayer) + 1);
    }


    // ── Symbols: atlas + per-symbol Spines ───────────────────────────────────────────────────
    this.symbolCatalog = RuntimeSymbolCatalog.fromConfig({ entries: SYMBOL_CATALOG, atlas: assets.symbols, log });
    const spineSources = new Map<string, SymbolSpineSource>();
    for (const entry of SYMBOL_SPINE_CATALOG) {
      const data = assets.skeletons.get(entry.skeletonPath);
      if (data) spineSources.set(entry.symbolId, { skeletonData: data, scale: entry.scale, idle: entry.idle });
      else log(`[GameScene] symbol spine "${entry.skeletonPath}" (${entry.symbolId}) not loaded`);
    }
    this.symbolSpineSources = spineSources;

    // ── Per-round factories ──────────────────────────────────────────────────────────────────
    this.createWinAmountLabel = goldenFont
      ? createWinAmountLabelFactory({
          glowTexture: assets.textures.glow,
          font: goldenFont,
          explosionSkeleton: assets.skeletons.get("animations/spine/number_explosion.skeleton"),
          spineDriver: input.spineDriver,
        })
      : null;
    if (!this.createWinAmountLabel) log("[GameScene] goldenFont not loaded — no win-amount labels");

    const vfxBoard = assets.skeletons.get("animations/spine/vfx_board.skeleton");
    this.createAnticipationFrame = vfxBoard
      ? () => {
          const spine = new Spine({ skeletonData: vfxBoard, autoUpdate: false });
          spine.label = "vfx_board";
          input.spineDriver.attach(spine);
          return new AnticipationFrameView({ spine, chromeRunner });
        }
      : null;

    this.removalEffectSpawner = assets.sheets.removalBurst
      ? createRemovalEffectSpawner({ sheet: assets.sheets.removalBurst, parent: this.gridContainer.fxUnderlays, ticker: input.presentationTicker })
      : null;
  }

  public dispose(): void {
    for (const d of this.disposers) d();
    this.removalEffectSpawner?.destroy();
    this.gridContainer.dispose();
    this.background.dispose();
    this.destroy({ children: true });
  }
}
